import { CaptionsRenderer } from '@sparemin/text-effects';
import React from 'react';
import { isEqual, omit } from 'underscore';

import useFontLoadedListener from 'hooks/useFontLoadedListener';
import useThrottledIntegerEmit from 'hooks/useThrottledIntegerEmit';
import { Size } from 'types';
import {
  createCaptionCueObject,
  getDefaultCaptionWordStyling,
} from 'utils/caption-adv-animation';

import { useCanvasSize } from '../../context/CanvasSizeContext';
import { CaptionsState } from '../../types';
import { getAnimationTextStyles } from './utils';

interface UseCaptionsAssetAnimationsConfig {
  captionsContent: string;
  captionsOverride?: CaptionsState;
  externalObservableData?: unknown;
}

interface UseCaptionsAssetAnimations {
  isResizing: boolean;
  onResize: (newSize: Size<number>) => void;
  onResizeStart: () => void;
  onResizeEnd: () => void;
  wrapperNodeRef: React.MutableRefObject<HTMLDivElement>;
}

const useCaptionsAssetAnimations = (
  config: UseCaptionsAssetAnimationsConfig,
): UseCaptionsAssetAnimations => {
  const { isFontsLoaded } = useFontLoadedListener();

  const { captionsContent, captionsOverride } = config;

  const pickedCaptionsOverride = omit(captionsOverride, ['size', 'position']);
  const [
    prevPickedCaptionOverride,
    setPrevPickedCaptionsOverride,
  ] = React.useState(pickedCaptionsOverride);

  const [isResizing, setIsResizing] = React.useState(false);

  const wrapperNodeRef = React.useRef<HTMLDivElement>(null);
  const captionsRendererRef = React.useRef<CaptionsRenderer | undefined>();

  const { canvas, toPx } = useCanvasSize();

  const boxHeightPx = React.useMemo(() => {
    const measurement = captionsOverride?.size?.height;

    if (!measurement) {
      return undefined;
    }

    return toPx(measurement)?.value;
  }, [captionsOverride, toPx]);

  /**
   * Initializes the animation.
   */
  React.useEffect(() => {
    if (
      !isFontsLoaded ||
      !wrapperNodeRef.current ||
      !captionsOverride?.advancedAnimation?.enabled ||
      captionsRendererRef.current
    ) {
      return;
    }

    captionsRendererRef.current = new CaptionsRenderer(wrapperNodeRef.current, {
      cues: [createCaptionCueObject(captionsContent)],
      textStyles: getAnimationTextStyles(captionsOverride),
      animationConfig: captionsOverride?.advancedAnimation,
      ...(canvas?.width &&
        canvas?.height && {
          canvasDims: canvas,
        }),
      ...(boxHeightPx && {
        cueOpts: { height: boxHeightPx },
      }),
      ...getDefaultCaptionWordStyling(canvas?.width),
    });

    requestAnimationFrame(() => {
      captionsRendererRef?.current?.getAnimator();
    });
  }, [
    boxHeightPx,
    canvas,
    captionsContent,
    captionsOverride,
    isFontsLoaded,
    isResizing,
  ]);

  const handleUpdateConfig = React.useCallback(
    (
      nextCaptionsOverride: CaptionsState,
      nextCaptionsContent: string,
      nextCanvas?: Size<number>,
      skipConfig?: boolean,
      nextBoxHeightPx?: number,
    ): void => {
      requestAnimationFrame(() => {
        captionsRendererRef?.current?.updateConfig({
          cues: nextCaptionsOverride?.advancedAnimation.enabled
            ? [createCaptionCueObject(nextCaptionsContent)]
            : [],
          textStyles: getAnimationTextStyles(nextCaptionsOverride),
          animationConfig: !skipConfig
            ? nextCaptionsOverride?.advancedAnimation
            : undefined,
          ...(nextCanvas?.width &&
            nextCanvas?.height && {
              canvasDims: nextCanvas,
            }),
          ...(nextBoxHeightPx && {
            cueOpts: {
              height: nextBoxHeightPx,
            },
          }),
          ...getDefaultCaptionWordStyling(nextCanvas?.width),
        });
      });
    },
    [],
  );

  if (!isEqual(prevPickedCaptionOverride, pickedCaptionsOverride)) {
    setPrevPickedCaptionsOverride(pickedCaptionsOverride);

    handleUpdateConfig(
      pickedCaptionsOverride,
      captionsContent,
      canvas,
      false,
      boxHeightPx,
    );
  }

  /**
   * Applies a live box height to the renderer with the animation config
   * skipped to avoid animator restart flicker.
   */
  const applyLiveBoxHeight = React.useCallback(
    (nextBoxHeightPx: number): void => {
      handleUpdateConfig(
        captionsOverride,
        captionsContent,
        canvas,
        true,
        nextBoxHeightPx,
      );
    },
    [canvas, captionsContent, captionsOverride, handleUpdateConfig],
  );

  const liveResize = useThrottledIntegerEmit({
    onEmit: applyLiveBoxHeight,
  });

  /**
   * Live resize handler called with the rendered box size on every tick.
   */
  const handleResize = React.useCallback(
    (newSize: Size<number>): void => {
      if (newSize?.height) {
        liveResize.update(newSize.height);
      }
    },
    [liveResize],
  );

  /**
   * When the resizing starts the animation config is removed, this prevents
   * the animations consequent restarts with size updates to make the text
   * fit not to work properly. This can be seen mostly at the highlight not
   * matching the text properly.
   */
  const handleResizeStart = React.useCallback((): void => {
    setIsResizing(true);

    handleUpdateConfig(
      captionsOverride,
      captionsContent,
      canvas,
      true,
      boxHeightPx,
    );
  }, [
    boxHeightPx,
    canvas,
    captionsContent,
    captionsOverride,
    handleUpdateConfig,
  ]);

  /**
   * When resize ends, the animations config is restored.
   */
  const handleResizeEnd = React.useCallback((): void => {
    setIsResizing(false);
    liveResize.reset();

    handleUpdateConfig(
      captionsOverride,
      captionsContent,
      canvas,
      false,
      boxHeightPx,
    );
  }, [
    boxHeightPx,
    canvas,
    captionsContent,
    captionsOverride,
    handleUpdateConfig,
    liveResize,
  ]);

  return {
    isResizing,
    onResize: handleResize,
    onResizeEnd: handleResizeEnd,
    onResizeStart: handleResizeStart,
    wrapperNodeRef,
  };
};

export default useCaptionsAssetAnimations;
